import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@frontend/shadcn/components/ui/avatar';
import { Button } from '@frontend/shadcn/components/ui/button';
import { ArrowDownToLineIcon, ArrowUpRightFromSquareIcon } from '@revfluence/fresh-icons/solid/esm';
import { ContentPreviewUserProps } from '../types';

export const CreatorAvatar = React.forwardRef<
  React.ElementRef<typeof Avatar>,
  React.ComponentPropsWithoutRef<typeof Avatar> & {
    name: string;
    avatarUrl?: string;
  }
>(({ name, avatarUrl, className, ...props }, ref) => {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <Avatar ref={ref} className={className} {...props}>
      <AvatarImage src={avatarUrl} alt={name} />
      <AvatarFallback>{initials}</AvatarFallback>
    </Avatar>
  );
});
CreatorAvatar.displayName = 'CreatorAvatar';

export const CreatorInfo = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    name: string;
    handle: string;
  }
>(({ name, handle, className, ...props }, ref) => (
  <div ref={ref} className="flex flex-col" {...props}>
    <span className="font-medium">{name}</span>
    <span className="text-sm text-muted-foreground">@{handle}</span>
  </div>
));
CreatorInfo.displayName = 'CreatorInfo';

export const CreatorActionDownload = React.forwardRef<HTMLButtonElement, React.ComponentPropsWithoutRef<typeof Button>>(
  (props, ref) => (
    <Button ref={ref} variant="ghost" size="icon" aria-label="Download content" {...props}>
      <ArrowDownToLineIcon className="h-4 w-4" />
    </Button>
  ),
);
CreatorActionDownload.displayName = 'CreatorActionDownload';

export const CreatorActionShare = React.forwardRef<HTMLButtonElement, React.ComponentPropsWithoutRef<typeof Button>>(
  (props, ref) => (
    <Button ref={ref} variant="ghost" size="icon" aria-label="Open in new window" {...props}>
      <ArrowUpRightFromSquareIcon className="h-4 w-4" />
    </Button>
  ),
);
CreatorActionShare.displayName = 'CreatorActionShare';

export const CreatorActionsContainer = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={`flex items-center gap-2 ${className}`} {...props} />,
);
CreatorActionsContainer.displayName = 'CreatorActionsContainer';

export const CreatorContainer = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={`flex items-center justify-between ${className}`} {...props} />
  ),
);
CreatorContainer.displayName = 'CreatorContainer';

export const CreatorAvatarContainer = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={`flex items-center gap-3 ${className}`} {...props} />,
);
CreatorAvatarContainer.displayName = 'CreatorAvatarContainer';

export function ContentPreviewUser({ creator, onDownload, onShare }: ContentPreviewUserProps) {
  const handleShare = () => {
    if (onShare) {
      onShare();
    } else {
      window.open(window.location.href, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <CreatorContainer>
      <CreatorAvatarContainer>
        <CreatorAvatar name={creator.name} avatarUrl={creator.avatarUrl} />
        <CreatorInfo name={creator.name} handle={creator.handle} />
      </CreatorAvatarContainer>
      <CreatorActionsContainer>
        <CreatorActionDownload onClick={onDownload} />
        <CreatorActionShare onClick={handleShare} />
      </CreatorActionsContainer>
    </CreatorContainer>
  );
}

export default ContentPreviewUser;
